import { useEffect, useState } from 'react';
import { useTimer } from '../../contexts/TimerContext';
import {
  getAgeWeeks,
  getWakeWindow,
  getLastSleepEndTime,
  getWakeStatus,
  getTypicalBedtime,
  getBedtimeConsistency,
  getDailySleepTarget,
  getTodaySleepMinutes,
  getTodayNapStats,
  formatMinutes,
  formatBedtime,
  WAKE_WINDOW_CUTOFF_WEEKS,
} from '../../utils/wakeWindowUtils';
import { formatTimeAgo } from '../../utils/dateUtils';

// ── Status config ──────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  'too-early': {
    emoji: '😴',
    color: 'var(--text-light)',
    bg:    'var(--bg-light)',
    label: (s) => `Nap window opens in ~${formatMinutes(s.minutesUntilWindow)}`,
  },
  'approaching': {
    emoji: '⏰',
    color: 'var(--chip-amber-text, #92400e)',
    bg:    'var(--chip-amber-bg,  #fef3c7)',
    label: () => 'Nap window approaching',
  },
  'open': {
    emoji: '🟢',
    color: 'var(--chip-green-text)',
    bg:    'var(--chip-green-bg)',
    label: () => 'Nap window is open',
  },
  'overtired': {
    emoji: '⚠️',
    color: 'var(--chip-red-text)',
    bg:    'var(--chip-red-bg)',
    label: () => 'Nap window has passed',
  },
};

// ── Bedtime consistency label ──────────────────────────────────────────────

function consistencyLabel(stdDev) {
  if (stdDev <= 15) return { text: 'Very consistent',   color: 'var(--chip-green-text)', bg: 'var(--chip-green-bg)' };
  if (stdDev <= 30) return { text: 'Fairly consistent', color: 'var(--chip-amber-text, #92400e)', bg: 'var(--chip-amber-bg, #fef3c7)' };
  return              { text: 'Variable',              color: 'var(--chip-red-text)',   bg: 'var(--chip-red-bg)' };
}

// ── Component ──────────────────────────────────────────────────────────────

/**
 * WakeWindowCard — shows how long the baby has been awake since their last
 * nap and whether the next nap window is approaching, open, or overdue.
 * Also shows: sleep debt indicator, today's nap count, and bedtime consistency score.
 *
 * Props:
 *   dateOfBirth       {string}  YYYY-MM-DD or ISO string — from selectedChild.dateOfBirth
 *   sleepData         {Array}   recent sleep entries (last 48h) for wake window calculation
 *   extendedSleepData {Array}   last 14 days of sleep entries for bedtime average (optional)
 */
const WakeWindowCard = ({ dateOfBirth, sleepData, extendedSleepData }) => {
  const { timer, elapsed } = useTimer();
  const sleepTimerRunning  = timer?.type === 'sleep';

  // Tick every minute so the "awake X min" counter stays live
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const ageWeeks       = getAgeWeeks(dateOfBirth);
  const wakeWindow     = getWakeWindow(ageWeeks);
  const lastNapEnd     = getLastSleepEndTime(sleepData);
  const status         = getWakeStatus(wakeWindow, lastNapEnd, now);
  const typicalBedtime = getTypicalBedtime(extendedSleepData);
  const consistency    = getBedtimeConsistency(extendedSleepData);
  const sleepTarget    = getDailySleepTarget(ageWeeks);
  const todaySleepMin  = getTodaySleepMinutes(sleepData);
  const napStats       = getTodayNapStats(sleepData);

  // Minutes since midnight right now — for the bedtime countdown
  const nowDate          = new Date(now);
  const currentMinutes   = nowDate.getHours() * 60 + nowDate.getMinutes();
  const minutesToBedtime = typicalBedtime
    ? typicalBedtime.averageMinutes - currentMinutes
    : null;

  // ── Sleep timer running: show in-progress state, suppress all alerts ──
  if (sleepTimerRunning) {
    const elapsedMin = Math.floor(elapsed / 60);
    return (
      <div className="card" style={{ borderLeft: '4px solid var(--primary-dark)' }}>
        <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <span style={{ fontSize: '1.4rem', flexShrink: 0, animation: 'timer-dot-pulse 1.4s ease-in-out infinite', display: 'inline-block' }}>😴</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--primary-dark)' }}>
              Sleep in progress
            </div>
            <div style={{ fontSize: '0.82rem', color: 'var(--text-light)', marginTop: '0.1rem' }}>
              {elapsedMin > 0
                ? `${formatMinutes(elapsedMin)} so far — wake window will reset when sleep ends`
                : 'Timer just started — wake window will reset when sleep ends'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Child ≥ 2 years: informational only ────────────────────────────────
  if (ageWeeks != null && ageWeeks >= WAKE_WINDOW_CUTOFF_WEEKS) {
    return (
      <div className="card" style={{ borderLeft: '4px solid var(--border-light)' }}>
        <div className="card-body" style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '1.5rem', flexShrink: 0 }}>🌙</span>
          <div>
            <strong style={{ fontSize: '0.9rem' }}>Wake window guidance</strong>
            <p style={{ margin: '0.25rem 0 0', fontSize: '0.85rem', color: 'var(--text-light)' }}>
              Most children over 2 no longer need regular naps.
              You can hide this card via <strong>⚙ Customise</strong>.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── No DOB or wake window data: nothing to show ────────────────────────
  if (!wakeWindow) return null;

  const cfg = status ? STATUS_CONFIG[status.status] : null;

  // Sleep debt calculation
  const sleepDebtMin = sleepTarget != null ? sleepTarget - todaySleepMin : null;

  return (
    <div className="card" style={{ borderLeft: `4px solid ${cfg?.color ?? 'var(--border-light)'}` }}>
      <div className="card-body">

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '1.3rem' }}>⏱️</span>
            <h6 style={{ margin: 0, fontWeight: 700, fontSize: '0.9rem' }}>Wake Window</h6>
          </div>
          <span style={{
            fontSize: '0.75rem',
            color: 'var(--text-light)',
            background: 'var(--bg-light)',
            padding: '0.15rem 0.5rem',
            borderRadius: '50px',
          }}>
            {formatMinutes(wakeWindow.minMin)}–{formatMinutes(wakeWindow.maxMin)} at this age
          </span>
        </div>

        {/* No nap logged state */}
        {!lastNapEnd && (
          <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-light)' }}>
            No nap logged in the last 48 hours — log a sleep session to track wake windows.
          </p>
        )}

        {/* Active wake window status */}
        {status && cfg && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {/* Awake time */}
            <div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-light)', marginBottom: '0.1rem' }}>
                Awake for
              </div>
              <div style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--primary-dark)', lineHeight: 1 }}>
                {formatMinutes(status.awakeMin)}
              </div>
            </div>

            {/* Divider */}
            <div style={{ width: 1, height: 36, background: 'var(--border-light)', flexShrink: 0 }} />

            {/* Status badge + label */}
            <div>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.3rem',
                padding: '0.2rem 0.6rem',
                borderRadius: '50px',
                fontSize: '0.8rem',
                fontWeight: 600,
                background: cfg.bg,
                color: cfg.color,
                marginBottom: '0.2rem',
              }}>
                {cfg.emoji} {cfg.label(status)}
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>
                {status.status === 'too-early'  && `Window opens at ${formatMinutes(wakeWindow.minMin)}`}
                {status.status === 'approaching' && `Window opens in ~${formatMinutes(status.minutesUntilWindow)}`}
                {status.status === 'open'        && `Window closes at ${formatMinutes(wakeWindow.maxMin)}`}
                {status.status === 'overtired'   && `${formatMinutes(status.minutesOverdue)} past window`}
              </div>
            </div>
          </div>
        )}

        {/* ── Nap count strip ── */}
        <div style={{
          marginTop: '0.65rem',
          paddingTop: '0.6rem',
          borderTop: '1px solid var(--border-light)',
          display: 'flex',
          alignItems: 'center',
          gap: '0.4rem',
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: '0.85rem' }}>💤</span>
          <span style={{ fontSize: '0.82rem', color: 'var(--text-light)' }}>
            {napStats.count === 0
              ? 'No naps logged today'
              : `${napStats.count} nap${napStats.count !== 1 ? 's' : ''} today`}
          </span>
          {napStats.lastNapEnd && (
            <span style={{ fontSize: '0.78rem', color: 'var(--text-light)' }}>
              · last ended {formatTimeAgo(napStats.lastNapEnd)}
            </span>
          )}

          {/* Sleep debt — right-aligned */}
          {sleepTarget != null && (
            <span style={{ marginLeft: 'auto', fontSize: '0.78rem', color: sleepDebtMin > 30 ? 'var(--chip-amber-text, #92400e)' : 'var(--chip-green-text)' }}>
              {sleepDebtMin <= 0
                ? `✓ Target met (${formatMinutes(sleepTarget)})`
                : `${formatMinutes(sleepDebtMin)} below target`}
            </span>
          )}
        </div>

        {/* ── Typical bedtime + consistency score ── */}
        {typicalBedtime && (
          <div style={{
            marginTop: '0.6rem',
            paddingTop: '0.6rem',
            borderTop: '1px solid var(--border-light)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.4rem',
            flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: '0.85rem' }}>🌙</span>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-light)' }}>
              Typical bedtime
            </span>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-dark)' }}>
              ~{formatBedtime(typicalBedtime.averageMinutes)}
            </span>
            {/* Countdown: only shown within 3 hours of bedtime, and only if bedtime hasn't passed */}
            {minutesToBedtime !== null && minutesToBedtime > 0 && minutesToBedtime <= 180 && (
              <span style={{
                fontSize: '0.78rem',
                color: minutesToBedtime <= 30
                  ? 'var(--chip-amber-text, #92400e)'
                  : 'var(--text-light)',
                fontWeight: minutesToBedtime <= 30 ? 600 : 400,
              }}>
                · in {formatMinutes(minutesToBedtime)}
              </span>
            )}

            {/* Consistency badge */}
            {consistency && (() => {
              const c = consistencyLabel(consistency.stdDevMinutes);
              return (
                <span style={{
                  marginLeft: 'auto',
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  padding: '0.15rem 0.5rem',
                  borderRadius: '50px',
                  background: c.bg,
                  color: c.color,
                }}>
                  {c.text}
                </span>
              );
            })()}

            <span style={{ fontSize: '0.75rem', color: 'var(--text-light)', width: '100%' }}>
              avg of {typicalBedtime.sampleCount} nights
              {consistency && ` · ±${consistency.stdDevMinutes}m variation`}
            </span>
          </div>
        )}

      </div>
    </div>
  );
};

export default WakeWindowCard;
